import { z } from 'zod';
import { formatMoney } from './offers';

/**
 * Rozliczenie zgłoszenia serwisowego (docs/26): pozycje do zapłaty za serwis / przegląd — z cennika usług serwisowych
 * (katalog, pozycje oznaczone „cennik serwisu”) albo spoza cennika z ceną wpisaną przez serwisanta. Klient potwierdza
 * pozycje podpisem protokołu, płaci online (ING Pay: kod QR na miejscu albo link e-mailem), a po pełnej wpłacie CRM
 * wystawia fakturę VAT jako zapłaconą (Fakturownia).
 */

/** Pozycja rozliczenia (ceny brutto w groszach). */
export interface ServiceChargeLine {
  /** pozycja katalogu (cennik serwisu); null — pozycja spoza cennika */
  catalogItemId: string | null;
  name: string;
  unit: string;
  quantity: number;
  /** cena jednostkowa brutto */
  unitGrossGr: number;
  vatRate: number;
  /** przegląd w ramach gwarancji — pozycja 0 zł z dopiskiem */
  free?: boolean;
}

export interface ServiceChargeItemDto extends ServiceChargeLine {
  id: string;
  /** spoza cennika (catalogItemId = null) — oznaczenie dla biura */
  offList: boolean;
  /** cena inna niż w cenniku w chwili dodania (np. stary cennik na telefonie) */
  priceDiffers: boolean;
  /** biuro (przed wizytą) / teren (z podpisanego protokołu) */
  source: 'biuro' | 'teren';
  protocolCode: string | null;
  addedByName: string | null;
}

/** Pozycja cennika usług serwisowych (na telefonie i w biurze). */
export interface ServicePriceListItem {
  id: string;
  name: string;
  unit: string;
  kind: string;
  unitGrossGr: number;
  vatRate: number;
}

export const SERVICE_CHARGE_STATES = {
  brak: 'Bez pozycji do zapłaty',
  do_zaplaty: 'Do zapłaty',
  czesciowo: 'Zapłacono częściowo',
  oplacone: 'Opłacone',
} as const;
export type ServiceChargeState = keyof typeof SERVICE_CHARGE_STATES;

export const SERVICE_INVOICE_STATES = {
  brak: 'Faktura po wpłacie',
  wysylanie: 'Wystawianie faktury…',
  wystawiona: 'Faktura wystawiona',
  blad: 'Błąd wystawienia faktury',
  niepewna: 'Faktura do sprawdzenia w Fakturowni',
  reczna: 'Faktura wpisana ręcznie',
} as const;
export type ServiceInvoiceState = keyof typeof SERVICE_INVOICE_STATES;

/** Rozliczenie w oknie zgłoszenia (biuro). */
export interface ServiceChargeDto {
  ticketId: string;
  items: ServiceChargeItemDto[];
  totalGr: number;
  paidGr: number;
  toPayGr: number;
  state: ServiceChargeState;
  /** firma rozliczająca ma aktywne ING Pay (link / kod QR) */
  payOnline: boolean;
  /** ostatni link „Zapłać online” wysłany e-mailem */
  linkSentAt: string | null;
  linkSentTo: string | null;
  invoice: { state: ServiceInvoiceState; number: string | null; error: string; at: string | null };
  /** nadpłaty z płatności online (do zwrotu w panelu ING Pay) */
  overpayments: { id: string; amountGr: number; transactionId: string; createdAt: string; resolvedAt: string | null; resolvedByName: string | null; note: string }[];
  can: { edit: boolean; sendLink: boolean; invoice: boolean; resolveOverpayment: boolean };
}

/** Rozliczenie w pakiecie zadania serwisu / przeglądu (telefon serwisanta z uprawnieniem `serwis.platnosci`). */
export interface FieldServiceCharge {
  /** pozycje ustalone w biurze (przed wizytą) */
  items: ServiceChargeLine[];
  /** pozycje już potwierdzone podpisanymi protokołami tego zgłoszenia */
  confirmedTotalGr: number;
  paidGr: number;
  /** zgłoszenie gwarancyjne — płatne pozycje tylko z powodem */
  warranty: boolean;
  priceList: ServicePriceListItem[];
  /** firma ma aktywne ING Pay — „Zapłać przez ING Pay” (z siecią) */
  payOnline: boolean;
}

/** Rozliczenie w treści protokołu (podpisane przez klienta; pole tylko, gdy są pozycje). */
export interface ProtocolCharges {
  items: ServiceChargeLine[];
  totalGr: number;
  /** powód płatnych pozycji w zgłoszeniu gwarancyjnym (np. usterka z winy użytkownika) */
  reason?: string;
}

/** Wartość pozycji brutto (grosze). */
export const chargeLineGr = (l: Pick<ServiceChargeLine, 'quantity' | 'unitGrossGr' | 'free'>): number => (l.free ? 0 : Math.round(l.quantity * l.unitGrossGr));
export const chargeTotalGr = (lines: Pick<ServiceChargeLine, 'quantity' | 'unitGrossGr' | 'free'>[]): number => lines.reduce((s, l) => s + chargeLineGr(l), 0);
/** Netto pozycji z brutto i stawki (do faktury). */
export const grossToNetGr = (grossGr: number, vatRate: number): number => Math.round((grossGr * 100) / (100 + vatRate));

export const chargeLineSchema = z.object({
  catalogItemId: z.string().uuid().nullable(),
  name: z.string().trim().min(1, 'Podaj nazwę pozycji').max(200),
  unit: z.string().trim().min(1).max(10),
  quantity: z.number().positive('Ilość musi być większa od zera').max(100_000),
  unitGrossGr: z.number().int().min(0).max(100_000_000),
  vatRate: z.number().int().min(0).max(23),
  free: z.boolean().optional(),
});

/** Pozycje ustalane w biurze (zastępują wszystkie pozycje „biuro”). */
export const serviceChargeUpdateSchema = z.object({ items: z.array(chargeLineSchema).max(50) });
export type ServiceChargeUpdateInput = z.infer<typeof serviceChargeUpdateSchema>;

export const protocolChargesSchema = z.object({
  items: z.array(chargeLineSchema).min(1).max(50),
  totalGr: z.number().int().min(0),
  reason: z.string().trim().max(500).optional(),
});

/** Płatność ING Pay za serwis na telefonie (kod QR). */
export interface FieldServicePaymentDto {
  onlinePaymentId: string;
  url: string;
  qr: string;
  amountGr: number;
}
export interface FieldServicePaymentStateDto {
  status: string;
  totalGr: number;
  paidGr: number;
}

/** Ile minut po przyjęciu protokołu z pozycjami do zapłaty CRM sam wysyła klientowi link „Zapłać online”. */
export const SERVICE_PAY_LINK_AFTER_MIN = 30;

/** Opis pozycji w protokole i na fakturze. */
export function chargeLineText(l: ServiceChargeLine): string {
  const qty = String(l.quantity).replace('.', ',');
  return `${qty} ${l.unit} × ${formatMoney(l.unitGrossGr)}${l.free ? ' (w ramach gwarancji — 0 zł)' : ''}${l.catalogItemId ? '' : ' · spoza cennika'}`;
}
